import { useEffect, useRef } from 'react';
import type { FormInstance } from 'antd';
import { MeasureType, MeasurementUnitEnum } from 'domains/measurementDomain';
import { reexpress } from 'utils/measurement';
import { useMeasurementUnit } from './useMeasurementUnit';

/** The size fields a custom-size step holds, in every flavour of the step. */
const CUSTOM_SIZE_FIELDS = ['width', 'length', 'flatWidth', 'flatLength'];

/**
 * Re-expresses the custom-size fields when the viewer switches units.
 *
 * A number in an open form exists in one unit only: the page does not refetch when the unit is
 * switched, so leaving it under the new unit's label is what turned a typed 8.5x11in into
 * 8.5x11mm. Everything the API sends arrives in both units and is picked rather than converted -
 * this is only for what the user typed a moment ago.
 *
 * Shared rather than repeated: the three custom-size steps - Step3, E4 and the Albertson one -
 * are near copies of each other, and a fix applied to one of them is a fix missing from two.
 */
export const useCustomSizeUnit = (
  form: FormInstance,
  fields: string[] = CUSTOM_SIZE_FIELDS
) => {
  const { unit } = useMeasurementUnit();
  const shownIn = useRef<MeasurementUnitEnum>(unit);

  useEffect(() => {
    const from = shownIn.current;
    if (from === unit) {
      return;
    }
    shownIn.current = unit;

    const current = form.getFieldsValue(fields);
    const next: Record<string, number | undefined> = {};
    fields.forEach((field) => {
      const value = current?.[field];
      if (value !== undefined && value !== null && value !== '') {
        next[field] = reexpress(value, MeasureType.LENGTH, from, unit);
      }
    });
    if (Object.keys(next).length > 0) {
      form.setFieldsValue(next);
    }
  }, [unit, form, fields]);
};

/**
 * The size string to pre-fill a custom-size form from, in the unit the viewer reads.
 *
 * The spec's own size is the base one, in inches. Filling the fields from it and labelling them
 * with the viewer's unit is how reopening an 8.5x11in job showed 8.5x11mm.
 */
export const sizeForViewer = (
  imperial: string | null | undefined,
  metric: string | null | undefined,
  unit: MeasurementUnitEnum
): string => {
  const shown = unit === MeasurementUnitEnum.METRIC ? metric ?? imperial : imperial;
  return shown ?? '';
};

/** The size fields one row of a multi-part size table holds. */
const ROW_SIZE_FIELDS = ['finishedWidth', 'finishedLength', 'flatWidth', 'flatLength'];

/**
 * The Form.List flavour of {@link useCustomSizeUnit}, for the multi-part size table.
 *
 * Same reason, different shape: the rows live under one list field, so each row's own size fields
 * have to be re-expressed rather than a flat pair.
 */
export const useCustomSizeListUnit = (
  form: FormInstance,
  listName: string,
  fields: string[] = ROW_SIZE_FIELDS
) => {
  const { unit } = useMeasurementUnit();
  const shownIn = useRef<MeasurementUnitEnum>(unit);

  useEffect(() => {
    const from = shownIn.current;
    if (from === unit) {
      return;
    }
    shownIn.current = unit;

    const rows = form.getFieldValue(listName);
    if (!Array.isArray(rows) || rows.length === 0) {
      return;
    }
    const converted = rows.map((row) => {
      if (!row || typeof row !== 'object') {
        return row;
      }
      const next = { ...row };
      fields.forEach((field) => {
        const value = next[field];
        if (value !== undefined && value !== null && value !== '') {
          next[field] = reexpress(value, MeasureType.LENGTH, from, unit);
        }
      });
      return next;
    });
    form.setFieldsValue({ [listName]: converted });
  }, [unit, form, listName, fields]);
};
